import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingServiceAPI } from '../services/bookingServiceAPI';
import '../styles/PaymentFailed.css';

function PaymentFailed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<any>(null);
  const [error, setError] = useState('');
  const [failureReason, setFailureReason] = useState('');

  useEffect(() => {
    const bookingId = searchParams.get('booking');
    const status = searchParams.get('status');
    const paymentId = searchParams.get('payment_id');

    if (!bookingId) {
      navigate('/');
      return;
    }

    loadBookingData(bookingId, status, paymentId);
  }, [searchParams, navigate]);

  const loadBookingData = async (bookingId: string, status: string | null, paymentId: string | null) => {
    try {
      setLoading(true);
      
      // جلب معلومات الحجز
      const bookingData = await bookingServiceAPI.getBooking(bookingId);
      
      if (!bookingData || !bookingData.booking) {
        setError('الحجز غير موجود');
        setLoading(false);
        return;
      }

      setBooking(bookingData.booking);

      // تحديد سبب فشل الدفع
      let reason = 'فشل في عملية الدفع';
      switch (status?.toUpperCase()) {
        case 'FAILED':
          reason = 'تم رفض العملية من البنك';
          break;
        case 'DECLINED':
          reason = 'تم رفض البطاقة';
          break;
        case 'CANCELLED':
          reason = 'تم إلغاء العملية';
          break;
        case 'EXPIRED':
          reason = 'انتهت صلاحية العملية';
          break;
        default:
          reason = 'حدث خطأ أثناء معالجة الدفع';
      }
      
      setFailureReason(reason);
      setLoading(false);
    } catch (err) {
      console.error('Error loading booking data:', err);
      setError('خطأ في تحميل بيانات الحجز');
      setLoading(false);
    }
  };

  const retryPayment = () => {
    if (booking) {
      navigate(`/payment?booking=${booking._id}`);
    }
  };

  if (loading) {
    return (
      <div className="payment-failed-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>جاري التحقق من حالة الدفع...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-failed-container">
        <motion.div 
          className="error-container"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
          <div className="error-icon">❌</div>
          <h1>{error}</h1>
          <button onClick={() => navigate('/')} className="home-button">
            العودة للرئيسية
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="payment-failed-container">
      <motion.div 
        className="failed-card"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <motion.div 
          className="failed-icon"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          ⚠️
        </motion.div>
        
        <h1>فشل في عملية الدفع</h1>
        <p className="failure-reason">{failureReason}</p>
        
        {booking && (
          <div className="booking-details">
            <h3>تفاصيل الحجز</h3>
            <div className="detail-item">
              <span>رقم الحجز:</span>
              <strong>{booking.bookingCode}</strong>
            </div>
            <div className="detail-item">
              <span>الاسم:</span>
              <strong>{booking.customerName}</strong>
            </div>
            <div className="detail-item">
              <span>نوع الكابينة:</span>
              <strong>{booking.boothDisplayName || booking.boothType}</strong>
            </div>
            <div className="detail-item">
              <span>التاريخ:</span>
              <strong>{new Date(booking.eventDate).toLocaleDateString('ar-SA')}</strong>
            </div>
            <div className="detail-item">
              <span>المبلغ:</span>
              <strong>{booking.totalAmount} ريال</strong>
            </div>
          </div>
        )}

        <div className="failure-suggestions">
          <h3>ماذا يمكنك فعله؟</h3>
          <ul>
            <li>تأكد من أن لديك رصيد كافٍ في البطاقة</li>
            <li>تحقق من صحة بيانات البطاقة</li>
            <li>جرب استخدام بطاقة أخرى</li>
            <li>تواصل مع البنك إذا استمرت المشكلة</li>
          </ul>
        </div>

        <div className="failed-actions">
          <button onClick={retryPayment} className="retry-button">
            المحاولة مرة أخرى
          </button>
          <button onClick={() => navigate('/')} className="home-button">
            العودة للرئيسية
          </button>
        </div>

        <div className="support-info">
          <h3>تحتاج مساعدة؟</h3>
          <p>📱 واتساب: <a href="https://wa.me/966548559300">966548559300</a></p>
          <p>📧 إيميل: fotomista.sa@gmail.com</p>
          <p>📱 واتساب: +966503859150</p>
        </div>
      </motion.div>
    </div>
  );
}

export default PaymentFailed;